<template>
  <div>
    <Modal
      ref="modal"
      v-model="isModalVisible"
      :title="hideModalHeader ? '' : currentStepTitle"
      :hide-header="hideModalHeader"
      :aria-label="hideModalHeader ? currentStepTitle : ''"
      :hide-footer="currentStep === PLAN_STEP"
      :backdrop="'static'"
      :hide-close-button="true"
      :dialog-class="'modal-dialog-centered modal-dialog-scrollable'"
    >
      <!-- Step indicator (hidden when Other waitlist confirmed — terminal
           state). Driven by totalSteps rather than hardcoded, because the
           plan step makes the flow two or three steps long. -->
      <div v-if="!otherSubmitted" class="step-indicator">
        <template v-for="step in totalSteps" :key="step">
          <div
            v-if="step > 1"
            :class="['step-line', { active: currentStep >= step }]"
          ></div>
          <div :class="['step-dot', { active: currentStep >= step }]">{{ step }}</div>
        </template>
      </div>

      <!-- Step 1: Name + Native Language. The name question is skipped when
           the auth provider already supplied a name (Sign in with Apple /
           Google) — App Store Guideline 4 forbids re-asking for it. -->
      <div v-if="currentStep === 1">
        <div
          v-if="!otherSubmitted && !isOnOtherForm && !nameFromAuthProvider"
          class="mb-3"
        >
          <label for="nameInput" class="form-label">{{ $t('onboarding.newStudent.nameLabel') }}</label>
          <input
            type="text"
            class="form-control"
            id="nameInput"
            v-model="name"
            :class="{ 'is-invalid': nameTouched && !isValidName }"
            :placeholder="$t('onboarding.newStudent.namePlaceholder')"
            @blur="nameTouched = true"
          />
          <div class="invalid-feedback" v-if="nameTouched && !isValidName">
            {{ $t('onboarding.newStudent.validationName') }}
          </div>
        </div>
        <div
          v-if="existingWaitlistLanguage && !otherSubmitted && !isOnOtherForm"
          class="waitlist-banner"
          role="status"
        >
          <span class="waitlist-banner-icon" aria-hidden="true">📝</span>
          <span class="waitlist-banner-text">
            <i18n-t keypath="onboarding.newStudent.waitlistBanner" scope="global">
              <template #language>
                <strong>{{ existingWaitlistLanguage }}</strong>
              </template>
            </i18n-t>
          </span>
        </div>
        <div class="mb-3">
          <label v-if="!otherSubmitted && !isOnOtherForm" class="form-label">{{ $t('onboarding.newStudent.nativeLanguagePrompt') }}</label>
          <LanguagePickerGrid
            :selected-code="nativeLanguageCode"
            :geo-likely-code="geoLikelyCode"
            :show-other-card="true"
            :user-email="userEmail"
            :is-submitting-other="isSubmittingOther"
            :other-error="otherError"
            :other-submitted="otherSubmitted"
            :existing-typed-language="existingWaitlistLanguage"
            @pick="onNativePick"
            @other-submit="onOtherSubmit"
            @other-form-toggle="isOnOtherForm = $event"
          />
        </div>
      </div>

      <!-- Step 2: Learning Language + Reason + Proficiency -->
      <div v-if="currentStep === 2">
        <!-- Native language summary bar -->
        <div class="native-summary-bar">
          <span class="native-summary-label">{{ $t('onboarding.wizard.yourNativeLanguage') }}:</span>
          <span class="native-summary-value">{{ translatedNativeLanguageName }}</span>
          <a href="#" class="native-summary-edit" @click.prevent="goToStep1">{{ $t('onboarding.wizard.edit') }}</a>
        </div>

        <!-- Language selection -->
        <div class="form-section">
          <h6 class="section-title">{{ $t('onboarding.newLanguage.selectLanguage') }}</h6>
          <LanguagePickerGrid
            :selected-code="selectedLanguage"
            :disabled-base-codes="nativeLanguageCode ? [nativeLanguageCode] : []"
            :show-other-card="false"
            @pick="selectLanguage"
          />
        </div>

        <!-- Reason selection -->
        <div class="form-section" ref="reasonSection" data-test="reason-section">
          <h6 class="section-title">{{ $t('onboarding.newLanguage.whyLearning') }}</h6>
          <div class="reason-options">
            <div
              v-for="reason in reasons"
              :key="reason.key"
              :class="[
                'reason-option',
                { active: selectedReason === reason.key },
              ]"
              :data-test="'reason-' + reason.key"
              @click="selectReason(reason.key)"
            >
              {{ reason.label }}
            </div>
          </div>
        </div>

        <!-- Proficiency selection -->
        <div class="form-section" ref="proficiencySection" data-test="proficiency-section">
          <h6 class="section-title">{{ $t('onboarding.newLanguage.proficiencyLevel') }}</h6>
          <div class="proficiency-options">
            <div
              v-for="level in proficiencyLevels"
              :key="level.key"
              :class="[
                'proficiency-option',
                { active: selectedProficiency === level.key },
              ]"
              :data-test="'proficiency-' + level.key"
              @click="selectProficiency(level.key)"
            >
              {{ level.label }}
            </div>
          </div>
        </div>
      </div>

      <!-- Step 3: plan selection (the step only exists when
           planStepEnabled was true at step-2 submit). Selection only — the
           picker owns its own CTA, which is why the modal footer is hidden
           here, and no payment rail is touched until the wizard has closed. -->
      <div v-if="currentStep === PLAN_STEP">
        <OnboardingPlanPicker
          v-model="planChoice"
          :user-data="userData"
          @confirm="onPlanConfirmed"
        />
      </div>

      <template #footer>
        <div class="wizard-footer" v-if="!otherSubmitted && !isOnOtherForm">
          <!-- Step 1 footer -->
          <button
            v-if="currentStep === 1"
            type="button"
            class="btn btn-primary wizard-btn"
            @click="goToStep2"
            :disabled="!step1Valid"
          >
            {{ $t('common.continue') }}
          </button>

          <!-- Step 2 footer -->
          <template v-if="currentStep === 2">
            <button
              type="button"
              class="btn btn-outline-secondary wizard-btn"
              @click="goToStep1"
            >
              {{ $t('onboarding.wizard.back') }}
            </button>
            <button
              type="button"
              class="button submit wizard-btn"
              @click="submitForm"
              :disabled="!step2Valid"
            >
              {{ $t('onboarding.newLanguage.startLearning') }}
            </button>
          </template>
        </div>
      </template>
    </Modal>

    <GeoMismatchModal
      v-if="pendingMismatchPick && pendingMismatchSuggested"
      v-model="showGeoMismatchModal"
      :geo-country="geoCountry"
      :geo-flag-code="pendingMismatchSuggested"
      :picked-code="pendingMismatchPick"
      :suggested-code="pendingMismatchSuggested"
      @change-to-suggested="onMismatchChangeToSuggested"
      @keep-pick="onMismatchKeepPick"
      @dismiss="onMismatchDismiss"
    />
  </div>
</template>

<script>
import Modal from "./Modal.vue";
import LanguagePickerGrid from "./LanguagePickerGrid.vue";
import GeoMismatchModal from "./GeoMismatchModal.vue";
import OnboardingPlanPicker from "./OnboardingPlanPicker.vue";
import Languages from "@/data/Language";
import { authStore } from "@/store/auth";
import { mapLanguageNameToLocale, isSameBaseLanguage } from "@/locales/index.js";
import { getDatabase, ref, update, get } from "firebase/database";
import { getAuth } from "firebase/auth";
import { sanitizeProviderName } from "@/scripts/onboardingName.js";
import { providerNameStore } from "@/store/providerName.js";
import { platformBilling } from "@/scripts/platformBilling.js";
import { wasCheckoutLaunched } from "@/store/checkoutSession.js";
import { useOnboardingEvents } from "@/composables/useOnboardingEvents.js";
import { useScrollToSection } from "@/composables/useScrollToSection.js";
import {
  navigatorCountryFallback,
  geoLikelyCodeFor,
  pickedTriggersMismatch,
} from "@/scripts/geoMismatchRules.js";

// The optional plan step is always the last one. Named so the template reads
// as "the plan step" rather than as a bare 3.
const PLAN_STEP = 3;
// Canonical plan ids, matching src/store/pricing.js and the RTDB .validate
// allowlist on users/{uid}/onboarding/planChoice.
const DEFAULT_PLAN_CHOICE = "free";
// v1 sells one price per plan: the picker has no cycle toggle and shows the
// monthly figure, so the hand-off is told monthly explicitly rather than being
// left to assume it. "month"/"year" is the spelling the rest of the app uses
// on the Stripe side (AccountView and MultiLanguageUpsellModal both split
// "foundation-month"), and the host translates it for the store rails.
const PLAN_CYCLE = "month";

export default {
  name: "OnboardingWizard",
  components: {
    Modal,
    LanguagePickerGrid,
    GeoMismatchModal,
    OnboardingPlanPicker,
  },
  props: {
    initialStep: {
      type: Number,
      default: 1,
    },
    prefillName: {
      type: String,
      default: "",
    },
    prefillNativeLanguage: {
      type: String,
      default: "",
    },
    prefillSelectedLanguage: {
      type: String,
      default: null,
    },
    // The users/{uid} snapshot, when the host has one. Read for the plan
    // step only: the picker states the account's EFFECTIVE free limits from
    // it, and an account that already has billing or an entitlement is never
    // asked to pick a plan.
    userData: {
      type: Object,
      default: null,
    },
  },
  emits: ["completed"],
  setup() {
    return { events: useOnboardingEvents(), ...useScrollToSection() };
  },
  data() {
    // Name already supplied by the auth provider (Apple's authorization sheet,
    // Google's profile): read from the Firebase user, falling back to the
    // in-memory stash for the session where the profile write failed.
    // Sanitized leniently — a provider name must be accepted as-is, never
    // bounced into a manual correction (App Store Guideline 4).
    const currentUser = getAuth().currentUser;
    const derivedProviderName = sanitizeProviderName(
      currentUser?.displayName || providerNameStore.getFor(currentUser?.uid)
    );
    return {
      isModalVisible: false,
      currentStep: this.initialStep,
      PLAN_STEP,
      // The plan the picker currently has selected. Display/analytics state
      // only: it is never an entitlement, here or anywhere downstream.
      planChoice: DEFAULT_PLAN_CHOICE,
      // null until step 2 is submitted, then the frozen answer to "does this
      // flow have a plan step?". Freezing it at that instant is what keeps a
      // mid-wizard billing update from renumbering the dots under the user.
      // Before the freeze the indicator previews the live answer, so the dot count the
      // user sees on steps 1-2 is the one they will actually walk.
      frozenPlanStepEnabled: null,
      name: this.prefillName || derivedProviderName,
      // Remount-safe: after step 1 writes the name to RTDB, DashboardView
      // remounts the wizard at step 2 with prefillName = the stored name. If
      // that still equals the provider name, "Edit" back to step 1 keeps the
      // field hidden; a user-typed name keeps showing as before.
      nameFromAuthProvider:
        derivedProviderName !== "" &&
        (this.prefillName === "" || this.prefillName === derivedProviderName),
      nameTouched: false,
      nativeLanguage: this.prefillNativeLanguage,
      // WHICH native language is known to be in RTDB, not merely whether one
      // is. A boolean cannot survive the user going back and changing the
      // selection: it would either call a superseded value stored, or refuse to
      // adopt a value that a still-pending write had in fact already persisted.
      // Populated on arrival when the wizard opened at step 2, because the
      // prefill was read back out of RTDB; otherwise only step 1's write sets
      // it. The locale cache must never describe a language RTDB does not hold.
      persistedNativeLanguage:
        this.initialStep === 2 ? this.prefillNativeLanguage || null : null,
      selectedLanguage: this.prefillSelectedLanguage || null,
      selectedReason: null,
      selectedProficiency: null,
      // Geo + mismatch state
      geoCountry: null,
      geoSource: "unknown",
      mismatchAlreadyShown: false,
      showGeoMismatchModal: false,
      pendingMismatchPick: null,
      pendingMismatchSuggested: null,
      mismatchModalOpenedAt: null,
      // Other waitlist state
      isSubmittingOther: false,
      otherError: "",
      otherSubmitted: false,
      isOnOtherForm: false,
      existingWaitlistLanguage: "",
      // Timing
      gridFirstPaintAt: null,
    };
  },
  computed: {
    currentStepTitle() {
      if (this.currentStep === PLAN_STEP) {
        return this.$t("onboarding.planPicker.title");
      }
      return this.currentStep === 1
        ? this.$t("onboarding.newStudent.title")
        : this.$t("onboarding.newLanguage.title");
    },
    // The plan step renders its own heading, so a modal title header would
    // print it twice; the dialog keeps its accessible name through aria-label
    // instead, exactly as the Other-waitlist terminal state does.
    hideModalHeader() {
      return this.otherSubmitted || this.currentStep === PLAN_STEP;
    },
    // Whether a plan step belongs in this flow AT ALL. Read once, at step-2
    // submit (see frozenPlanStepEnabled); until then it is the preview the
    // step indicator shows.
    //
    // tierSelectionOnboarding once sat first in this list; retired 2026-09-02 after being on for everyone.
    // The two skips, in the order they can be known:
    //   - this page session already launched a checkout, so the user is in
    //     the middle of buying and must not be offered a second purchase;
    //   - the account already carries billing or an entitlement, so the
    //     question is answered.
    planStepAvailable() {
      if (wasCheckoutLaunched(authStore.getUserId())) return false;
      const data = this.userData;
      if (data && typeof data === "object") {
        if (data.billing != null || data.features != null) return false;
      }
      return true;
    },
    planStepEnabled() {
      return this.frozenPlanStepEnabled === null
        ? this.planStepAvailable
        : this.frozenPlanStepEnabled;
    },
    totalSteps() {
      return this.planStepEnabled ? PLAN_STEP : 2;
    },
    nativeLanguageCode() {
      if (!this.nativeLanguage) return null;
      return mapLanguageNameToLocale(this.nativeLanguage);
    },
    translatedNativeLanguageName() {
      if (!this.nativeLanguageCode) return "";
      return this.$t(`languages.${this.nativeLanguageCode}`);
    },
    isValidName() {
      const nameRegex = /^[\p{L}\p{M}]+(?:[- ]?[\p{L}\p{M}]+)*$/u;
      return (
        nameRegex.test(this.name) &&
        this.name.trim().length >= 2 &&
        this.name.trim().length <= 30
      );
    },
    step1Valid() {
      // Provider-supplied names bypass the typed-input regex: "Liam O'Connor"
      // from Apple must not force a manual correction (Guideline 4). The
      // strict rule only governs names the user actually types.
      return (
        Boolean(this.nativeLanguage) &&
        (this.nameFromAuthProvider || this.isValidName)
      );
    },
    step2Valid() {
      return (
        this.selectedLanguage &&
        this.selectedReason &&
        this.selectedProficiency &&
        !isSameBaseLanguage(this.selectedLanguage, this.nativeLanguageCode)
      );
    },
    reasons() {
      return [
        { key: "career", label: this.$t("onboarding.newLanguage.reasons.career") },
        { key: "travel", label: this.$t("onboarding.newLanguage.reasons.travel") },
        { key: "fun", label: this.$t("onboarding.newLanguage.reasons.fun") },
        { key: "relationships", label: this.$t("onboarding.newLanguage.reasons.relationships") },
        { key: "culture", label: this.$t("onboarding.newLanguage.reasons.culture") },
        { key: "school", label: this.$t("onboarding.newLanguage.reasons.school") },
        { key: "other", label: this.$t("onboarding.newLanguage.reasons.other") },
      ];
    },
    proficiencyLevels() {
      return [
        { key: "beginner", label: this.$t("onboarding.newLanguage.proficiency.beginner") },
        { key: "elementary", label: this.$t("onboarding.newLanguage.proficiency.elementary") },
        { key: "intermediate", label: this.$t("onboarding.newLanguage.proficiency.intermediate") },
        { key: "upperIntermediate", label: this.$t("onboarding.newLanguage.proficiency.upperIntermediate") },
        { key: "advanced", label: this.$t("onboarding.newLanguage.proficiency.advanced") },
      ];
    },
    userEmail() {
      return getAuth().currentUser?.email || "";
    },
    geoLikelyCode() {
      return geoLikelyCodeFor(this.geoCountry);
    },
  },
  watch: {
    nativeLanguageCode(newCode) {
      // Clear learning-language selection if it now shares a base with native.
      if (
        this.selectedLanguage &&
        isSameBaseLanguage(this.selectedLanguage, newCode)
      ) {
        this.selectedLanguage = null;
      }
    },
  },
  methods: {
    async loadGeoAndMismatchFlag() {
      // Read mismatch idempotency flag + any existing waitlist entry from RTDB
      try {
        const userId = authStore.getUserId();
        if (userId) {
          const flagRef = ref(
            getDatabase(),
            `users/${userId}/onboarding/geoMismatchModalShown`,
          );
          const snap = await get(flagRef);
          if (snap.exists() && snap.val() === true) {
            this.mismatchAlreadyShown = true;
          }

          const waitlistRef = ref(
            getDatabase(),
            `unsupportedLanguageWaitlist/${userId}`,
          );
          const waitlistSnap = await get(waitlistRef);
          if (waitlistSnap.exists()) {
            const entry = waitlistSnap.val() || {};
            if (entry.typedLanguage) {
              this.existingWaitlistLanguage = entry.typedLanguage;
            }
          }
        }
      } catch (err) {
        console.warn("Failed to read onboarding state from RTDB:", err);
      }

      // Detect geo: try cloud function first, fall back to navigator.language.
      try {
        const country = await platformBilling.detectRegion();
        if (country && typeof country === "string" && country.length === 2) {
          this.geoCountry = country.toUpperCase();
          this.geoSource = "detectRegion";
          return;
        }
      } catch (err) {
        console.warn("detectRegion failed, falling back to navigator:", err);
      }
      const fallback = navigatorCountryFallback();
      this.geoCountry = fallback.country;
      this.geoSource = fallback.source;
    },
    onNativePick(code) {
      const previousCode = this.nativeLanguageCode;
      const lang = Languages[code];
      if (!lang) return;

      this.nativeLanguage = lang.name;

      this.events.trackNativePick({
        userId: authStore.getUserId() || "",
        gmail: this.userEmail,
        geoCountry: this.geoCountry,
        geoSource: this.geoSource,
        ipLocale: this.$i18n.locale,
        pickedLang: code,
        positionInGrid: this.computeGridPosition(code),
        timeToPickMs: this.gridFirstPaintAt
          ? Date.now() - this.gridFirstPaintAt
          : -1,
      });

      if (previousCode && previousCode !== code) {
        this.events.trackNativeChange({
          userId: authStore.getUserId() || "",
          fromLang: previousCode,
          toLang: code,
          trigger: "manual_tap",
        });
      }

      // Geo-mismatch modal trigger (fires at most once per user).
      if (!this.mismatchAlreadyShown) {
        const suggested = pickedTriggersMismatch(this.geoCountry, code);
        if (suggested) {
          this.pendingMismatchPick = code;
          this.pendingMismatchSuggested = suggested;
          this.showGeoMismatchModal = true;
          this.mismatchModalOpenedAt = Date.now();
          this.markMismatchShown();
        }
      }
    },
    computeGridPosition(code) {
      const codes = Object.keys(Languages);
      const geo = this.geoLikelyCode;
      const sorted = [...codes].sort((a, b) => {
        if (geo) {
          if (a === geo) return -1;
          if (b === geo) return 1;
        }
        return (Languages[a]?.nativeScriptName || "").localeCompare(
          Languages[b]?.nativeScriptName || "",
        );
      });
      return sorted.indexOf(code);
    },
    async markMismatchShown() {
      this.mismatchAlreadyShown = true;
      try {
        const userId = authStore.getUserId();
        if (!userId) return;
        await update(ref(getDatabase(), `users/${userId}/onboarding`), {
          geoMismatchModalShown: true,
        });
      } catch (err) {
        console.warn("Failed to persist geoMismatchModalShown flag:", err);
      }
    },
    onMismatchChangeToSuggested() {
      const fromCode = this.pendingMismatchPick;
      const toCode = this.pendingMismatchSuggested;
      const lang = Languages[toCode];
      if (lang) {
        this.nativeLanguage = lang.name;
        this.events.trackNativeChange({
          userId: authStore.getUserId() || "",
          fromLang: fromCode,
          toLang: toCode,
          trigger: "modal",
        });
      }
      this.events.trackGeoMismatchModal({
        userId: authStore.getUserId() || "",
        geo: this.geoCountry,
        picked: fromCode,
        suggested: toCode,
        outcome: "changed_to_suggested",
        timeOnModalMs: this.mismatchModalOpenedAt
          ? Date.now() - this.mismatchModalOpenedAt
          : -1,
      });
      this.pendingMismatchPick = null;
      this.pendingMismatchSuggested = null;
      this.mismatchModalOpenedAt = null;
    },
    onMismatchKeepPick() {
      this.events.trackGeoMismatchModal({
        userId: authStore.getUserId() || "",
        geo: this.geoCountry,
        picked: this.pendingMismatchPick,
        suggested: this.pendingMismatchSuggested,
        outcome: "kept_pick",
        timeOnModalMs: this.mismatchModalOpenedAt
          ? Date.now() - this.mismatchModalOpenedAt
          : -1,
      });
      this.pendingMismatchPick = null;
      this.pendingMismatchSuggested = null;
      this.mismatchModalOpenedAt = null;
    },
    onMismatchDismiss() {
      this.events.trackGeoMismatchModal({
        userId: authStore.getUserId() || "",
        geo: this.geoCountry,
        picked: this.pendingMismatchPick,
        suggested: this.pendingMismatchSuggested,
        outcome: "dismissed_via_close_button",
        timeOnModalMs: this.mismatchModalOpenedAt
          ? Date.now() - this.mismatchModalOpenedAt
          : -1,
      });
      this.pendingMismatchPick = null;
      this.pendingMismatchSuggested = null;
      this.mismatchModalOpenedAt = null;
    },
    async onOtherSubmit({ typedLanguage }) {
      const userId = authStore.getUserId();
      if (!userId) {
        this.otherError = this.$t("onboarding.newStudent.otherCard.submitError");
        return;
      }

      this.isSubmittingOther = true;
      this.otherError = "";

      const path = `unsupportedLanguageWaitlist/${userId}`;
      let isRetry = false;
      try {
        const snap = await get(ref(getDatabase(), path));
        isRetry = snap.exists();
      } catch {
        // Read failure: treat as first-time submission; not load-bearing.
      }

      const payload = {
        gmail: this.userEmail.toLowerCase(),
        typedLanguage: typedLanguage.slice(0, 100),
        geo: this.geoCountry || "",
        timestamp: Date.now(),
      };

      const writeOnce = () =>
        update(ref(getDatabase(), path), payload);

      try {
        await writeOnce();
      } catch (firstError) {
        console.warn("Waitlist write failed; retrying in 1s:", firstError);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        try {
          await writeOnce();
        } catch (secondError) {
          console.error("Waitlist write failed twice:", secondError);
          this.otherError = this.$t(
            "onboarding.newStudent.otherCard.submitError",
          );
          this.isSubmittingOther = false;
          return;
        }
      }

      this.events.trackOtherSubmitted({
        userId,
        gmail: this.userEmail,
        typedLanguage,
        geo: this.geoCountry,
        isRetry,
      });

      this.existingWaitlistLanguage = typedLanguage;
      this.isSubmittingOther = false;
      this.otherSubmitted = true;
    },
    goToStep2() {
      if (!this.step1Valid) return;

      // Drop any pending in-page scroll before swapping the step's content.
      this.cancelScroll();

      const forUserId = authStore.getUserId();
      // Captured, not read again in the callbacks. The user can go back and
      // pick a different language while this write is pending, and a callback
      // that reads this.nativeLanguage would then report the value on screen as
      // persisted when what actually landed in RTDB is this one.
      const submittedNativeLanguage = this.nativeLanguage;
      const userRef = ref(getDatabase(), `users/${forUserId}`);
      update(userRef, {
        state: null,
        name: this.name.trim(),
        nativeLanguage: submittedNativeLanguage,
      })
        .then(() => {
          console.debug("User data updated successfully");
          // This step is what makes nativeLanguage true in RTDB, so it is also
          // what must refresh the locale and its cache. Waiting until the final
          // submit left an account that abandoned onboarding at step 2 with a
          // nativeLanguage the UI and the preferredLocale cache did not match.
          //
          // This value is now what RTDB holds, whatever the user is looking at.
          this.persistedNativeLanguage = submittedNativeLanguage;
          // Adopted only when the selection has not moved on. If it has, this
          // value is stored but stale and the on-screen one is current but not
          // stored, so neither is safe to put in the locale cache. A later
          // write, or the final submit, settles it once they agree again.
          if (this.nativeLanguage === submittedNativeLanguage) {
            authStore.setNativeLanguage(submittedNativeLanguage, { forUserId });
          }
        })
        .catch((error) => {
          console.error("Error updating user data:", error);
          // Left untouched on purpose: RTDB still holds whatever it held
          // before, and this failure says nothing about what that was.
        });

      this.currentStep = 2;
      this.resetModalScroll();
    },
    goToStep1() {
      // Drop any pending in-page scroll before swapping the step's content.
      this.cancelScroll();
      this.currentStep = 1;
      this.resetModalScroll();
    },
    /* Both steps render inside the same scrollable modal body, so the outgoing
       step's scroll offset would otherwise carry into the incoming one (scroll
       down on step 1 to reach a native language, land mid-page on step 2).
       Wait for the new step to render before resetting, or the offset is
       clamped against the old content's height. */
    resetModalScroll() {
      this.$nextTick(() => {
        if (this.$refs.modal) this.$refs.modal.scrollToTop();
      });
    },
    selectLanguage(code) {
      if (isSameBaseLanguage(code, this.nativeLanguageCode)) return;
      this.selectedLanguage = code;
      // Nudge the next unanswered question into view (skipped at fire time if
      // reason is already chosen — e.g. re-tapping a flag after answering).
      this.scrollToSection(this.$refs.reasonSection, () => !this.selectedReason);
    },
    selectReason(key) {
      this.selectedReason = key;
      this.scrollToSection(
        this.$refs.proficiencySection,
        () => !this.selectedProficiency,
      );
    },
    selectProficiency(key) {
      this.selectedProficiency = key;
    },
    submitForm() {
      if (!this.step2Valid) return;

      // No pending scroll should fire against the closing modal.
      this.cancelScroll();

      const userId = authStore.getUserId();
      const updates = {};
      updates[`/users/${userId}/selectedLanguage`] = this.selectedLanguage;
      updates[
        `/users/${userId}/languagePreferences/${this.selectedLanguage}/proficiency`
      ] = this.selectedProficiency;
      updates[
        `/users/${userId}/languagePreferences/${this.selectedLanguage}/reasonForLearning`
      ] = this.selectedReason;

      update(ref(getDatabase()), updates)
        .then(() => {
          console.debug("Language preferences saved successfully");
        })
        .catch((error) => {
          console.error("Error saving language preferences:", error);
        });

      // Covers the user who reached this screen with a prefilled step 1
      // (initialStep === 2) and so never passed through goToStep2, and the one
      // whose step-1 write landed while the selection was temporarily something
      // else; idempotent for everyone else. Gated on the selection matching
      // what RTDB actually holds, because this submit writes selectedLanguage
      // and preferences, NOT nativeLanguage -- it cannot persist the value it
      // would be adopting.
      if (this.nativeLanguage && this.nativeLanguage === this.persistedNativeLanguage) {
        authStore.setNativeLanguage(this.nativeLanguage, { forUserId: userId });
      }

      // The one moment the plan-step decision is made. Frozen here rather
      // than re-read later so that a webhook adding a billing node mid-flow
      // changes nothing for a wizard already past this line.
      this.frozenPlanStepEnabled = this.planStepAvailable;
      if (this.frozenPlanStepEnabled) {
        this.currentStep = PLAN_STEP;
        this.resetModalScroll();
        this.events.trackPlanViewed({ userId: userId || "" });
        return;
      }

      this.finishWizard();
    },
    // The picker's CTA. The plan is recorded and the wizard closes; nothing
    // is purchased here, and the host decides what a paid pick means once the
    // modal is gone.
    onPlanConfirmed(planId) {
      const userId = authStore.getUserId();
      this.planChoice = planId;
      this.events.trackPlanSelected({ userId: userId || "", plan: planId });
      this.recordPlanChoice(userId, planId);
      this.finishWizard();
    },
    // Fire-and-forget by design. planChoice is display/analytics data — the
    // admin panel and the funnel read it, nothing branches on it — so a
    // rejected write must not hold a user inside onboarding.
    recordPlanChoice(userId, planId) {
      if (!userId) return;
      update(ref(getDatabase(), `users/${userId}/onboarding`), {
        planChoice: planId,
      }).catch((error) => {
        console.warn("Failed to persist onboarding planChoice:", error);
      });
    },
    // The single exit. The payload reports what was picked and nothing more:
    // the wizard never purchases and never grants, so a paid pick is a request
    // the host acts on once this modal is gone. A flow that skipped the plan
    // step reports the free default, which is the inert value the host reads
    // as "launch nothing" — the same outcome as picking free deliberately.
    finishWizard() {
      this.isModalVisible = false;
      this.$emit("completed", { plan: this.planChoice, cycle: PLAN_CYCLE });
    },
  },
  mounted() {
    this.isModalVisible = true;
    this.gridFirstPaintAt = Date.now();
    // If starting at step 2 with prefills, mark name as touched so validation shows correctly
    if (this.initialStep === 2 && this.prefillName) {
      this.nameTouched = true;
    }
    this.loadGeoAndMismatchFlag();
  },
  beforeUnmount() {
    // A delayed scroll must not fire against a detached section.
    this.cancelScroll();
  },
};
</script>

<style scoped>
/* Step indicator */
.step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.step-dot {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background-color: var(--al-surface-sunken);
  color: var(--al-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 600;
  transition: all var(--al-dur-medium) var(--al-ease-move);
}

.step-dot.active {
  background-color: var(--al-primary);
  color: var(--al-white);
}

.step-line {
  width: 3rem;
  height: 2px;
  background-color: var(--al-border);
  margin: 0 0.5rem;
  transition: background-color var(--al-dur-medium) var(--al-ease-move);
}

.step-line.active {
  background-color: var(--al-primary);
}

/* Waitlist acknowledgment banner. AA: --al-amber text on white/soft
   backgrounds is ~2.15:1 (fails), so the copy uses gray-700 instead. */
.waitlist-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.75rem 0.875rem;
  margin-bottom: 1rem;
  background-color: color-mix(in srgb, var(--al-amber) 15%, var(--al-surface-raised));
  border: 1px solid var(--al-amber);
  border-radius: var(--al-radius);
  font-size: 0.875rem;
  color: var(--al-text);
  line-height: 1.4;
}

.waitlist-banner-icon {
  flex-shrink: 0;
  font-size: 1.125rem;
  line-height: 1;
}

.waitlist-banner-text {
  flex: 1;
  min-width: 0;
  word-wrap: break-word;
}

/* Native language summary bar */
.native-summary-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background-color: var(--al-surface-sunken);
  border-radius: var(--al-radius);
  margin-bottom: 1.25rem;
  font-size: 0.9rem;
}

.native-summary-label {
  color: var(--al-text-muted);
  font-weight: 500;
}

.native-summary-value {
  color: var(--al-text);
  font-weight: 600;
}

/* AA: --al-primary text on white is ~3.33:1 (fails for normal-size text), so
   this link uses --al-primary-hover (~4.67:1) instead. */
.native-summary-edit {
  margin-left: auto;
  color: var(--al-primary-hover);
  font-weight: 500;
  text-decoration: none;
  font-size: 0.85rem;
}

.native-summary-edit:hover {
  text-decoration: underline;
}

/* Form sections */
.form-section {
  margin-bottom: 1.5rem;
  /* Auto-scroll targets: keep the section heading clear of the sticky modal
     chrome after scrollIntoView({ block: 'start' }) lands (Unit 2). */
  scroll-margin-top: 1rem;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--al-text);
  margin-bottom: 0.75rem;
}

/* Reason & proficiency pills */
.reason-options,
.proficiency-options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.reason-option,
.proficiency-option {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0.5rem 1rem;
  background-color: var(--al-surface-sunken);
  border-radius: var(--al-radius-pill);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all var(--al-dur-short) var(--al-ease-move);
  border: 2px solid transparent;
}

.reason-option:hover,
.proficiency-option:hover {
  background-color: var(--al-border);
}

/* Selected-chip recipe: primary border + primary-soft bg + primary-text role
   (not primary text on the soft bg, which measures ~2.7:1 and fails AA). */
.reason-option.active,
.proficiency-option.active {
  background-color: var(--al-primary-soft);
  border-color: var(--al-primary);
  color: var(--al-text);
  font-weight: 500;
}

/* Wizard footer */
.wizard-footer {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
}

.wizard-btn {
  min-width: 120px;
}

/* Submit button */
.button.submit {
  background-color: var(--al-primary);
  color: var(--al-white);
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: var(--al-radius);
  font-weight: 600;
  transition: all var(--al-dur-short) var(--al-ease-move);
}

.button.submit:hover:not(:disabled) {
  background-color: var(--al-primary-hover);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px color-mix(in srgb, var(--al-primary-hover) 20%, transparent);
}

.button.submit:active:not(:disabled) {
  transform: translateY(0);
}

.button.submit:disabled {
  background-color: var(--al-light-blue);
  cursor: not-allowed;
}

/* Responsive styles */
@media (max-width: 576px) {
  .reason-option,
  .proficiency-option {
    font-size: 0.85rem;
    padding: 0.4rem 0.8rem;
  }

  .native-summary-bar {
    flex-wrap: wrap;
    font-size: 0.85rem;
  }

  .wizard-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .wizard-btn {
    width: 100%;
  }
}
</style>
